<template>
    <div class="panorama-container" :style="containerStyle">
      <!-- 加载中状态 -->
      <div v-if="loading" class="loading-overlay">
        <div class="loading-spinner"></div>
        <div class="loading-text">全景图加载中...</div>
        <div class="loading-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: loadProgress + '%' }"></div>
          </div>
          <div class="progress-text">{{ loadProgress }}%</div>
        </div>
      </div>
  
      <!-- 加载失败状态 -->
      <div v-if="loadError" class="error-overlay">
        <div class="error-icon">⚠️</div>
        <div class="error-text">{{ errorMessage }}</div>
        <button v-if="src" class="retry-btn" @click="retryLoad">重新加载</button>
      </div>
  
      <!-- 全景图内容 -->
      <div v-show="!loading && !loadError" class="panorama-content">
        <div class="panorama-viewer" ref="viewerContainer">
          <canvas ref="canvas"></canvas>
          
          <!-- 批注组件 -->
          <UAVAnnotation
            v-if="enableAnnotation"
            ref="annotationComponent"
            :annotations="annotations"
            :current-user-id="currentUserId"
            :enable-drag="true"
            :show-actions="true"
            :can-reply="isUserLoggedIn"
            @drag-start="startDragAnnotation"
            @annotation-click="showAnnotationDetail"
            @delete-annotation="deleteAnnotation"
            @delete-reply="deleteReply"
            @save-reply="handleSaveReply"
          />
        </div>
  
        <!-- 新建批注弹窗 -->
        <div v-if="showNewAnnotation" class="annotation-modal" @click="cancelNewAnnotation">
          <div class="annotation-content" @click.stop>
            <div class="annotation-header-modal">
              <h3>添加批注</h3>
              <button class="close-btn" @click="cancelNewAnnotation">×</button>
            </div>
            <div class="annotation-body">
              <textarea 
                v-model="newAnnotationText" 
                placeholder="请输入批注内容..."
                class="annotation-input"
                rows="4"
                ref="annotationInput"
              ></textarea>
            </div>
            <div class="annotation-footer">
              <button class="btn-cancel" @click="cancelNewAnnotation">取消</button>
              <button class="btn-save" @click="saveNewAnnotation">保存</button>
            </div>
          </div>
        </div>
  
        <!-- 右上角模式切换按钮 -->
        <div v-if="fastUrl" class="mode-toggle-btn" @click="toggleMode">
          {{ useFastMode ? '原图模式' : '快速模式' }}
        </div>

        <!-- 底部浮动工具栏 -->
        <div class="floating-toolbar">
          <button 
            v-if="isUserLoggedIn"
            class="toolbar-btn"
            :class="{ 'active': annotationMode }"
            @click="toggleAnnotationMode"
            title="批注模式"
          >
            <span class="icon">{{ annotationMode ? '✓' : '📝' }}</span>
          </button>
        </div>
  
        <!-- 提示信息 -->
        <div v-if="annotationMode && enableAnnotation" class="tip-banner">
          💡 批注模式已开启，点击全景图任意位置添加批注
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import * as THREE from 'three';
  import UAVAnnotation from './annotation.vue';
  
  export default {
    name: 'UAVPanorama',
    components: {
      UAVAnnotation,
    },
    props: {
      // 全景图片地址
      src: {
        type: String,
        required: true,
      },
      // 快速加载URL（压缩图）
      fastUrl: {
        type: String,
        default: '',
      },
      // 容器宽度
      width: {
        type: [String, Number],
        default: null,
      },
      // 容器高度
      height: {
        type: [String, Number],
        default: null,
      },
      // 项目ID
      projectId: {
        type: [String, Number],
        default: null,
      },
      // 视图ID
      viewId: {
        type: [String, Number],
        default: null,
      },
      // 场景ID
      sceneId: {
        type: [String, Number],
        default: null,
      },
      // 配置选项
      option: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        scene: null,
        camera: null,
        renderer: null,
        sphere: null,
        raycaster: null,
        isUserInteracting: false,
        onMouseDownMouseX: null,
        onMouseDownMouseY: null,
        lon: 0,
        onMouseDownLon: 0,
        lat: 0,
        useFastMode: true, // 默认使用快速模式（如果有fast_url）
        onMouseDownLat: 0,
        phi: 0,
        theta: 0,
        autoRotateEnabled: false,
        annotationMode: false,
        annotations: [],
        selectedAnnotation: null,
        showNewAnnotation: false,
        newAnnotationText: '',
        pendingAnnotation: null,
        animationId: null,
        draggingAnnotation: null,
        isDraggingAnnotation: false,
        loading: true,
        loadError: false,
        errorMessage: '',
        loadProgress: 0,
        abortController: null,
        lastEmittedRotation: { x: 0, y: 0, z: 0, zoom: 75 },
        replyingTo: null,
        replyText: '',
        showAllReplies: false,
      };
    },
    computed: {
      // 容器样式
      containerStyle() {
        const style = {};
        
        if (this.width) {
          style.width = typeof this.width === 'number' ? `${this.width}px` : this.width;
        } else {
          style.width = '100%';
        }
        
        if (this.height) {
          style.height = typeof this.height === 'number' ? `${this.height}px` : this.height;
        } else {
          style.height = '100%';
        }
        
        return style;
      },
      // 是否用户已登录
      isUserLoggedIn() {
        try {
          const session = this.$store.getters['session/session'];
          return !!(session && session.id);
        } catch (error) {
          console.warn('无法获取用户登录状态:', error);
          return false;
        }
      },
      // 当前用户ID
      currentUserId() {
        try {
          const session = this.$store.getters['session/session'];
          return session ? session.id : null;
        } catch (error) {
          return null;
        }
      },
      // 是否启用批注功能
      enableAnnotation() {
        return this.option.enableAnnotation !== false;
      },
      // 初始经度
      initialLon() {
        return this.option.initialLon || 0;
      },
      // 初始纬度
      initialLat() {
        return this.option.initialLat || 0;
      },
      // 初始视场角
      initialFov() {
        return this.option.initialFov || 75;
      },
      // 自动旋转速度
      autoRotateSpeed() {
        return this.option.autoRotateSpeed || 0.1;
      },
      // 球体半径
      sphereRadius() {
        return this.option.sphereRadius || 500;
      },
      // 球体分段数
      sphereSegments() {
        return this.option.sphereSegments || { width: 128, height: 64 };
      },
      // 显示的回复列表（最近3条或全部）
      displayedReplies() {
        if (!this.selectedAnnotation || !this.selectedAnnotation.replies) {
          return [];
        }
        if (this.showAllReplies || this.selectedAnnotation.replies.length <= 3) {
          return this.selectedAnnotation.replies;
        }
        // 只显示最近3条
        return this.selectedAnnotation.replies.slice(-3);
      },
    },
    watch: {
      src(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.retryLoad();
        }
      },
      'option.x': function(val) {
        if (val !== undefined && !this.isUserInteracting) {
          this.lon = val;
        }
      },
      'option.y': function(val) {
        if (val !== undefined && !this.isUserInteracting) {
          this.lat = val;
        }
      },
      'option.zoom': function(val) {
        if (val !== undefined && this.camera) {
          this.camera.fov = val;
          this.camera.updateProjectionMatrix();
        }
      },
    },
    mounted() {
      // 设置初始角度（优先使用option中的x, y值）
      this.lon = this.option.x !== undefined ? this.option.x : this.initialLon;
      this.lat = this.option.y !== undefined ? this.option.y : this.initialLat;
      this.autoRotateEnabled = this.option.autoRotate !== false;
      
      // 使用 $nextTick 确保 DOM 完全渲染后再初始化 Three.js
      this.$nextTick(() => {
        this.initThreeJS();
        if (this.enableAnnotation && this.viewId) {
          this.$api.afterLogin(() => {
            this.loadAnnotations();
          });
        }
      });
      
      window.addEventListener('resize', this.onWindowResize);
    },
    beforeDestroy() {
      
      // 取消正在进行的加载
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      // 停止动画循环
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }

      // 清理事件监听器
      window.removeEventListener('resize', this.onWindowResize);
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
      document.removeEventListener('mousemove', this.onDragAnnotation);
      document.removeEventListener('mouseup', this.endDragAnnotation);
      
      // 清理 Three.js 对象
      if (this.sphere) {
        // 清理纹理
        if (this.sphere.material && this.sphere.material.map) {
          this.sphere.material.map.dispose();
          this.sphere.material.map = null;
        }
        
        // 清理材质
        if (this.sphere.material) {
          this.sphere.material.dispose();
          this.sphere.material = null;
        }
        
        // 清理几何体
        if (this.sphere.geometry) {
          this.sphere.geometry.dispose();
          this.sphere.geometry = null;
        }
        
        // 从场景移除
        if (this.scene) {
          this.scene.remove(this.sphere);
        }
        
        this.sphere = null;
      }
      
      // 清理场景中的所有对象
      if (this.scene) {
        while (this.scene.children.length > 0) {
          const object = this.scene.children[0];
          this.scene.remove(object);
          
          // 递归清理对象
          if (object.geometry) {
            object.geometry.dispose();
          }
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(material => {
                if (material.map) material.map.dispose();
                material.dispose();
              });
            } else {
              if (object.material.map) object.material.map.dispose();
              object.material.dispose();
            }
          }
        }
        this.scene = null;
      }
      
      // 清理渲染器
      if (this.renderer) {
        this.renderer.dispose();
        this.renderer.forceContextLoss();
        this.renderer.domElement = null;
        this.renderer = null;
      }
      
      // 清理相机
      this.camera = null;
      
    },
    methods: {
      initThreeJS() {
        const container = this.$refs.viewerContainer;
        const canvas = this.$refs.canvas;
  
        if (!container || !canvas) {
          this.showError('初始化失败：容器未找到');
          return;
        }
  
        try {
          // 获取容器尺寸，如果为0则使用默认值
          const width = container.clientWidth || 800;
          const height = container.clientHeight || 600;
  
          // 创建场景
          this.scene = new THREE.Scene();
  
          // 创建相机
          this.camera = new THREE.PerspectiveCamera(
            this.initialFov,
            width / height,
            0.1,
            1000
          );
          this.camera.position.set(0, 0, 0);
  
          // 创建射线投射器（用于3D点击检测）
          this.raycaster = new THREE.Raycaster();
  
          // 创建渲染器
          this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
          this.renderer.setSize(width, height);
          this.renderer.setPixelRatio(window.devicePixelRatio);
  
          // 加载全景图纹理
          this.loadPanorama();
  
          // 添加事件监听
          canvas.addEventListener('mousedown', this.onMouseDown);
          document.addEventListener('mousemove', this.onMouseMove);
          document.addEventListener('mouseup', this.onMouseUp);
          canvas.addEventListener('wheel', this.onMouseWheel);
          canvas.addEventListener('touchstart', this.onTouchStart);
          canvas.addEventListener('touchmove', this.onTouchMove);
          canvas.addEventListener('touchend', this.onTouchEnd);
        } catch (error) {
          console.error('Three.js 初始化失败:', error);
          this.showError('初始化失败：' + error.message);
        }
      },
  
      async loadPanorama() {
        if (!this.src) {
          this.showError('未提供图片地址');
          return;
        }

        // 取消之前的加载请求
        if (this.abortController) {
          this.abortController.abort();
        }

        // 创建新的 AbortController
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        this.loading = true;
        this.loadError = false;
        this.loadProgress = 0;

        let simulatedProgressInterval = null;

        // 根据模式选择URL：如果有fast_url且使用快速模式，则使用fast_url
        const fetchUrl = (this.fastUrl && this.useFastMode) ? this.fastUrl : this.src;

        try {
          // 使用 fetch 加载图片
          const response = await fetch(fetchUrl, { signal });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          // 获取文件总大小
          const contentLength = response.headers.get('content-length');
          const total = contentLength ? parseInt(contentLength, 10) : 0;

          // 如果没有 content-length，使用模拟进度
          let simulatedProgress = 0;
          
          if (total === 0) {
            // 启动模拟进度（缓慢增长到90%）
            simulatedProgressInterval = setInterval(() => {
              if (simulatedProgress < 90) {
                simulatedProgress += Math.random() * 10;
                simulatedProgress = Math.min(simulatedProgress, 90);
                this.loadProgress = Math.round(simulatedProgress);
                this.$emit('progress', this.loadProgress);
              }
            }, 200);
          }

          // 读取数据流
          const reader = response.body.getReader();
          let chunks = [];
          let receivedLength = 0;
          let chunksAll = null;

          while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            chunks.push(value);
            receivedLength += value.length;

            // 更新进度
            if (total > 0) {
              const progress = Math.round((receivedLength / total) * 100);
              this.loadProgress = progress;
              this.$emit('progress', progress);
            }
          }
          
          // 清除模拟进度定时器
          if (simulatedProgressInterval) {
            clearInterval(simulatedProgressInterval);
          }
          

          // 合并所有数据块
          chunksAll = new Uint8Array(receivedLength);
          let position = 0;
          for (const chunk of chunks) {
            chunksAll.set(chunk, position);
            position += chunk.length;
          }

          // 创建 Blob 和 URL
          const blob = new Blob([chunksAll]);
          const imageUrl = URL.createObjectURL(blob);

          // 使用 THREE.TextureLoader 加载 Blob URL
          const textureLoader = new THREE.TextureLoader();
          const texture = await new Promise((resolve, reject) => {
            textureLoader.load(
              imageUrl,
              (tex) => resolve(tex),
              undefined,
              (err) => reject(err)
            );
          });

          // 释放 Blob URL（立即释放）
          URL.revokeObjectURL(imageUrl);
          
          // 清空 Blob 数据引用，帮助垃圾回收
          chunksAll = null;

          // 检查是否被取消
          if (signal.aborted) {
            console.log('加载已取消');
            // 清理纹理
            if (texture) {
              texture.dispose();
            }
            return;
          }

          // 优化纹理设置
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();

          // 创建球体几何体
          const geometry = new THREE.SphereGeometry(
            this.sphereRadius,
            this.sphereSegments.width,
            this.sphereSegments.height
          );
          // 翻转几何体，使纹理在内部可见
          geometry.scale(-1, 1, 1);

          // 创建材质
          const material = new THREE.MeshBasicMaterial({
            map: texture,
          });

          // 如果已有球体，先移除
          if (this.sphere) {
            this.scene.remove(this.sphere);
            this.sphere.geometry.dispose();
            this.sphere.material.dispose();
          }

          // 创建球体网格
          this.sphere = new THREE.Mesh(geometry, material);
          this.scene.add(this.sphere);

          this.loading = false;
          this.loadProgress = 100;
          
          // 确保渲染器尺寸正确（修复加载后黑屏问题）
          this.$nextTick(() => {
            this.onWindowResize();
            
            // 开始动画循环
            if (!this.animationId) {
              this.animate();
            }
          });

          // 触发加载完成事件
          this.$emit('load', { success: true });
          
          // 清理 AbortController
          this.abortController = null;

        } catch (error) {
          // 清除模拟进度定时器
          if (simulatedProgressInterval) {
            clearInterval(simulatedProgressInterval);
          }

          // 检查是否是用户取消的请求
          if (error.name === 'AbortError') {
            console.log('图片加载被用户取消');
            return;
          }

          console.error('加载全景图失败:', error);
          this.showError('加载全景图失败：' + error.message);
          this.loading = false;
          this.abortController = null;
        } finally {
          // 确保清理大对象引用
          // chunks = null;
          //chunksAll = null;
        }
      },
  
      showError(message) {
        this.loading = false;
        this.loadError = true;
        this.errorMessage = message;
        this.$emit('error', { message });
      },
  
      retryLoad() {
        this.loadPanorama();
      },
  
      animate() {
        this.animationId = requestAnimationFrame(this.animate);
  
        // 自动旋转功能已移除，仅保留手动调整
  
        this.lat = Math.max(-85, Math.min(85, this.lat));
        this.phi = THREE.MathUtils.degToRad(90 - this.lat);
        this.theta = THREE.MathUtils.degToRad(this.lon);
  
        const x = this.sphereRadius * Math.sin(this.phi) * Math.cos(this.theta);
        const y = this.sphereRadius * Math.cos(this.phi);
        const z = this.sphereRadius * Math.sin(this.phi) * Math.sin(this.theta);
  
        this.camera.lookAt(x, y, z);
  
        this.renderer.render(this.scene, this.camera);

        // 发射视角变化事件
        const currentRotation = {
          x: Math.round(this.lon * 100) / 100,
          y: Math.round(this.lat * 100) / 100,
          z: 0,
          zoom: Math.round(this.camera.fov * 100) / 100
        };
        
        // 只在值有变化时发射事件，避免过度触发
        if (
          currentRotation.x !== this.lastEmittedRotation.x ||
          currentRotation.y !== this.lastEmittedRotation.y ||
          currentRotation.zoom !== this.lastEmittedRotation.zoom
        ) {
          this.lastEmittedRotation = currentRotation;
          this.$emit('rotation-change', currentRotation);
        }

        // 实时更新批注位置（直接操作DOM，无延迟）
        if (this.annotations.length > 0) {
          this.updateAnnotationPositions();
        }
      },
  
      onMouseDown(event) {
        event.preventDefault();
  
        this.isUserInteracting = true;
        this.onMouseDownMouseX = event.clientX;
        this.onMouseDownMouseY = event.clientY;
        this.onMouseDownLon = this.lon;
        this.onMouseDownLat = this.lat;
      },
  
      onMouseMove(event) {
        if (this.isUserInteracting && this.onMouseDownMouseX !== null && this.onMouseDownMouseY !== null) {
          this.lon = (this.onMouseDownMouseX - event.clientX) * 0.1 + this.onMouseDownLon;
          this.lat = (event.clientY - this.onMouseDownMouseY) * 0.1 + this.onMouseDownLat;
        }
      },
  
      onMouseUp(event) {
        const wasInteracting = this.isUserInteracting;
        this.isUserInteracting = false;
  
        if (this.onMouseDownMouseX === null || this.onMouseDownMouseY === null) {
          return;
        }
  
        // 计算拖动距离
        const dragDistance = Math.sqrt(
          Math.pow(event.clientX - this.onMouseDownMouseX, 2) + 
          Math.pow(event.clientY - this.onMouseDownMouseY, 2)
        );
        
        // 如果有拖动操作（距离大于5px），停止自动旋转
        if (dragDistance >= 5) {
          this.autoRotateEnabled = false;
        }
        
        // 如果是批注模式且点击时没有拖动
        if (
          this.enableAnnotation &&
          this.annotationMode &&
          wasInteracting &&
          dragDistance < 5
        ) {
          this.addAnnotationAt3DPoint(event.clientX, event.clientY);
        }
      },
  
      onMouseWheel(event) {
        event.preventDefault();
        const fov = this.camera.fov + event.deltaY * 0.05;
        this.camera.fov = THREE.MathUtils.clamp(fov, 30, 90);
        this.camera.updateProjectionMatrix();
      },
  
      onTouchStart(event) {
        if (event.touches.length === 1) {
          event.preventDefault();
          this.onMouseDownMouseX = event.touches[0].pageX;
          this.onMouseDownMouseY = event.touches[0].pageY;
          this.onMouseDownLon = this.lon;
          this.onMouseDownLat = this.lat;
          this.isUserInteracting = true;
        }
      },
  
      onTouchMove(event) {
        if (event.touches.length === 1) {
          event.preventDefault();
          this.lon = (this.onMouseDownMouseX - event.touches[0].pageX) * 0.1 + this.onMouseDownLon;
          this.lat = (event.touches[0].pageY - this.onMouseDownMouseY) * 0.1 + this.onMouseDownLat;
        }
      },
  
      onTouchEnd(event) {
        this.isUserInteracting = false;
  
        if (event.changedTouches.length === 1 && this.onMouseDownMouseX !== null && this.onMouseDownMouseY !== null) {
          const dragDistance = Math.sqrt(
            Math.pow(event.changedTouches[0].pageX - this.onMouseDownMouseX, 2) + 
            Math.pow(event.changedTouches[0].pageY - this.onMouseDownMouseY, 2)
          );
  
          if (dragDistance >= 5) {
            this.autoRotateEnabled = false;
          }
  
          if (
            this.enableAnnotation &&
            this.annotationMode &&
            dragDistance < 5
          ) {
            this.addAnnotationAt3DPoint(
              event.changedTouches[0].pageX,
              event.changedTouches[0].pageY
            );
          }
        }
      },
  
      onWindowResize() {
        const container = this.$refs.viewerContainer;
        if (container && this.camera && this.renderer) {
          this.camera.aspect = container.clientWidth / container.clientHeight;
          this.camera.updateProjectionMatrix();
          this.renderer.setSize(container.clientWidth, container.clientHeight);
          
          // 窗口大小改变后更新批注位置
          if (this.annotations.length > 0) {
            this.$nextTick(() => {
              this.updateAnnotationPositions();
            });
          }
        }
      },

      // 实时更新批注位置（直接操作DOM，性能优化）
      updateAnnotationPositions() {
        if (!this.camera || !this.$refs.viewerContainer || !this.$refs.annotationComponent) {
          return;
        }

        const container = this.$refs.viewerContainer;
        // 通过子组件的$el访问批注标记
        const markers = this.$refs.annotationComponent.$el.querySelectorAll('.annotation-marker');

        this.annotations.forEach((annotation, index) => {
          const marker = markers[index];
          if (!marker || !annotation.position) {
            return;
          }

          // 解析 position（可能是字符串）
          let position = annotation.position;
          if (typeof position === 'string') {
            try {
              position = JSON.parse(position);
            } catch (e) {
              return;
            }
          }

          // 计算3D位置
          const vector = new THREE.Vector3(
            position.x,
            position.y,
            position.z
          );

          // 检查是否在相机视野内
          const cameraDirection = new THREE.Vector3();
          this.camera.getWorldDirection(cameraDirection);
          const vectorToAnnotation = vector.clone().normalize();
          const dotProduct = cameraDirection.dot(vectorToAnnotation);

          if (dotProduct < 0.1) {
            // 在视野背面，隐藏
            marker.style.visibility = 'hidden';
            return;
          }

          // 投影到屏幕空间
          vector.project(this.camera);

          const x = (vector.x * 0.5 + 0.5) * container.clientWidth;
          const y = (-(vector.y * 0.5) + 0.5) * container.clientHeight;

          // 直接更新DOM（使用 transform 性能更好）
          marker.style.visibility = 'visible';
          marker.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
        });
      },
  
  
      toggleAnnotationMode() {
        this.annotationMode = !this.annotationMode;
        
        if (this.annotationMode) {
          this.autoRotateEnabled = false;
        }
      },

      // 切换加载模式
      toggleMode() {
        this.useFastMode = !this.useFastMode;
        // 重新加载全景图
        this.loadPanorama();
      },
  
      addAnnotationAt3DPoint(clientX, clientY) {
        if (!this.sphere || !this.raycaster) {
          return;
        }
  
        const container = this.$refs.viewerContainer;
        const rect = container.getBoundingClientRect();
        
        const mouse = new THREE.Vector2();
        mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  
        this.raycaster.setFromCamera(mouse, this.camera);
        const intersects = this.raycaster.intersectObject(this.sphere);
  
        if (intersects.length > 0) {
          const point = intersects[0].point;
  
          this.pendingAnnotation = {
            position: {
              x: point.x,
              y: point.y,
              z: point.z,
            },
          };
  
          this.showNewAnnotation = true;
          
          this.$nextTick(() => {
            if (this.$refs.annotationInput) {
              this.$refs.annotationInput.focus();
            }
          });
        }
      },
  
      async saveNewAnnotation() {
        if (!this.newAnnotationText.trim()) {
          alert('请输入批注内容');
          return;
        }

        // 检查必需的ID参数
        if (!this.viewId) {
          alert('缺少必要参数：viewId');
          console.error('保存批注失败：缺少viewId', {
            projectId: this.projectId,
            viewId: this.viewId,
            sceneId: this.sceneId
          });
          return;
        }

        try {
          const data = {
            project_id: this.projectId,
            view_id: this.viewId,
            scene_id: this.sceneId,
            content: this.newAnnotationText,
            position: this.pendingAnnotation.position,
          };


          const response = await this.$api.post('uav/comments', data);
          
          // 修复：API响应数据在 response.data.data 中
          const commentData = response.data.data || response.data;
          
          const annotation = {
            id: commentData.id,
            content: data.content,
            position: data.position,
            timestamp: commentData.created_at || new Date().toISOString(),
            user_id: commentData.created_by,
            replies: [],
          };

          this.annotations.push(annotation);

          // 立即更新批注位置
          this.$nextTick(() => {
            this.updateAnnotationPositions();
          });

          this.newAnnotationText = '';
          this.showNewAnnotation = false;
          this.pendingAnnotation = null;

          this.$emit('annotation-add', annotation);
        } catch (error) {
          console.error('保存批注失败:', error);
          alert('保存批注失败，请重试');
        }
      },
  
      cancelNewAnnotation() {
        this.newAnnotationText = '';
        this.showNewAnnotation = false;
        this.pendingAnnotation = null;
      },
  
      getAnnotationPosition(annotation) {
        if (!this.camera || !annotation || !annotation.position) {
          console.warn('批注位置计算失败 - 缺少必要数据:', {
            hasCamera: !!this.camera,
            hasAnnotation: !!annotation,
            hasPosition: !!(annotation && annotation.position),
            annotation: annotation
          });
          return {
            left: '-1000px',
            top: '-1000px',
            visibility: 'hidden',
          };
        }

        // 检查 position 是否为字符串（需要解析）
        let position = annotation.position;
        if (typeof position === 'string') {
          try {
            position = JSON.parse(position);
          } catch (e) {
            console.error('批注位置解析失败:', position, e);
            return {
              left: '-1000px',
              top: '-1000px',
              visibility: 'hidden',
            };
          }
        }

        const vector = new THREE.Vector3(
          position.x,
          position.y,
          position.z
        );

        const cameraDirection = new THREE.Vector3();
        this.camera.getWorldDirection(cameraDirection);
        const vectorToAnnotation = vector.clone().normalize();
        const dotProduct = cameraDirection.dot(vectorToAnnotation);

        if (dotProduct < 0.1) {
          // 批注在视野背面，不显示
          return {
            left: '-1000px',
            top: '-1000px',
            visibility: 'hidden',
          };
        }

        vector.project(this.camera);

        const container = this.$refs.viewerContainer;
        if (!container) {
          return {
            left: '-1000px',
            top: '-1000px',
            visibility: 'hidden',
          };
        }

        const x = (vector.x * 0.5 + 0.5) * container.clientWidth;
        const y = (-(vector.y * 0.5) + 0.5) * container.clientHeight;

        return {
          left: `${x}px`,
          top: `${y}px`,
          visibility: 'visible',
        };
      },
  
      showAnnotationDetail(annotation) {
        if (this.isDraggingAnnotation) {
          return;
        }
        this.selectedAnnotation = annotation;
        this.showAllReplies = false;
        this.replyingTo = null;
        this.replyText = '';
      },
  
      closeAnnotationDetail() {
        this.selectedAnnotation = null;
        this.showAllReplies = false;
        this.replyingTo = null;
        this.replyText = '';
      },
  
      startReply(annotationId) {
        this.replyingTo = annotationId;
        this.replyText = '';
        this.$nextTick(() => {
          if (this.$refs.replyInput) {
            this.$refs.replyInput.focus();
          }
        });
      },
  
      cancelReply() {
        this.replyingTo = null;
        this.replyText = '';
      },
  
      async saveReply() {
        if (!this.replyText.trim()) {
          alert('请输入回复内容');
          return;
        }

        try {
          const data = {
            project_id: this.projectId,
            view_id: this.viewId,
            scene_id: this.sceneId,
            content: this.replyText,
            reply_id: this.replyingTo,
          };


          const response = await this.$api.post('uav/comments', data);
          
          // 修复：API响应数据在 response.data.data 中
          const replyData = response.data.data || response.data;
          
          const reply = {
            id: replyData.id,
            content: replyData.content,
            timestamp: replyData.created_at || new Date().toISOString(),
            user_id: replyData.user_id,
            user_name: replyData.user_name || '匿名用户',
            reply_id: replyData.reply_id,
          };

          // 添加回复到主评论
          if (this.selectedAnnotation && this.selectedAnnotation.id === this.replyingTo) {
            if (!this.selectedAnnotation.replies) {
              this.selectedAnnotation.replies = [];
            }
            this.selectedAnnotation.replies.push(reply);
          }

          // 同步更新 annotations 数组
          const annotation = this.annotations.find(a => a.id === this.replyingTo);
          if (annotation) {
            if (!annotation.replies) {
              annotation.replies = [];
            }
            annotation.replies.push(reply);
          }

          this.replyText = '';
          this.replyingTo = null;

          this.$emit('reply-add', reply);
        } catch (error) {
          console.error('保存回复失败:', error);
          alert('保存回复失败，请重试');
        }
      },

      async handleSaveReply({ annotationId, content }) {
        try {
          const data = {
            project_id: this.projectId,
            view_id: this.viewId,
            scene_id: this.sceneId,
            content: content,
            reply_id: annotationId,
          };


          const response = await this.$api.post('uav/comments', data);
          
          // 修复：API响应数据在 response.data.data 中
          const replyData = response.data.data || response.data;
          
          const reply = {
            id: replyData.id,
            content: replyData.content,
            timestamp: replyData.created_at || new Date().toISOString(),
            user_id: replyData.created_by,
            reply_id: replyData.reply_id,
          };

          // 同步更新 annotations 数组
          const annotation = this.annotations.find(a => a.id === annotationId);
          if (annotation) {
            if (!annotation.replies) {
              annotation.replies = [];
            }
            annotation.replies.push(reply);
          }

          this.$emit('reply-add', reply);
        } catch (error) {
          console.error('保存回复失败:', error);
          alert('保存回复失败，请重试');
        }
      },
  
      async deleteReply(replyId) {
        if (!confirm('确定要删除这条回复吗？')) {
          return;
        }
  
        try {
          await this.$api.delete(`uav/comments/${replyId}`);
          
          // 从选中的批注中删除回复
          if (this.selectedAnnotation && this.selectedAnnotation.replies) {
            const index = this.selectedAnnotation.replies.findIndex(r => r.id === replyId);
            if (index !== -1) {
              this.selectedAnnotation.replies.splice(index, 1);
            }
          }
  
          // 同步更新 annotations 数组
          this.annotations.forEach(annotation => {
            if (annotation.replies) {
              const index = annotation.replies.findIndex(r => r.id === replyId);
              if (index !== -1) {
                annotation.replies.splice(index, 1);
              }
            }
          });
  
          this.$emit('reply-delete', { id: replyId });
        } catch (error) {
          console.error('删除回复失败:', error);
          alert('删除回复失败，请重试');
        }
      },
  
      canDeleteComment(comment) {
        // 只有评论作者才能删除
        return this.isUserLoggedIn && comment.user_id === this.currentUserId;
      },
  
      startDragAnnotation(event, annotation) {
        event.preventDefault();
        event.stopPropagation();
        
        this.draggingAnnotation = annotation;
        this.isDraggingAnnotation = false;
        
        document.addEventListener('mousemove', this.onDragAnnotation);
        document.addEventListener('mouseup', this.endDragAnnotation);
      },
  
      onDragAnnotation(event) {
        if (!this.draggingAnnotation || !this.sphere) return;
        
        this.isDraggingAnnotation = true;
        
        const container = this.$refs.viewerContainer;
        const rect = container.getBoundingClientRect();
        
        const mouse = new THREE.Vector2();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  
        this.raycaster.setFromCamera(mouse, this.camera);
        const intersects = this.raycaster.intersectObject(this.sphere);
  
        if (intersects.length > 0) {
          const point = intersects[0].point;
          this.draggingAnnotation.position.x = point.x;
          this.draggingAnnotation.position.y = point.y;
          this.draggingAnnotation.position.z = point.z;
        }
      },
  
      async endDragAnnotation(event) {
        document.removeEventListener('mousemove', this.onDragAnnotation);
        document.removeEventListener('mouseup', this.endDragAnnotation);
        
        if (this.draggingAnnotation && this.isDraggingAnnotation) {
          // 保存拖拽后的批注位置
          try {
            await this.$api.patch(`uav/comments/${this.draggingAnnotation.id}`, {
              position: JSON.stringify(this.draggingAnnotation.position)
            });
            this.$emit('annotation-update', this.draggingAnnotation);
          } catch (error) {
            console.error('更新批注位置失败:', error);
            alert('更新批注位置失败');
          }
        }
        
        setTimeout(() => {
          this.draggingAnnotation = null;
          this.isDraggingAnnotation = false;
        }, 100);
      },
  
      async deleteAnnotation(id) {
        try {
          await this.$api.delete(`uav/comments/${id}`);
          
          const index = this.annotations.findIndex((a) => a.id === id);
          if (index !== -1) {
            const annotation = this.annotations[index];
            this.annotations.splice(index, 1);
            this.selectedAnnotation = null;
            this.$emit('annotation-delete', annotation);
          }
        } catch (error) {
          console.error('删除批注失败:', error);
          alert('删除批注失败，请重试');
        }
      },
  
      async loadAnnotations() {
        if (!this.viewId) {
          console.warn('未提供 viewId，无法加载批注');
          return;
        }

        try {
          const response = await this.$api.get(`uav/views/${this.viewId}/comments`);
          
          // 修复：API响应数据在 response.data.data 中
          const comments = response.data.data || response.data || [];
          
          // 按 reply_id 归组：主评论和回复分开
          const mainComments = [];
          const replyMap = {};

          comments.forEach(comment => {
            if (!comment.reply_id) {
              // 主评论
              // 解析 position（可能是字符串）
              let position = comment.position;
              if (typeof position === 'string') {
                try {
                  position = JSON.parse(position);
                } catch (e) {
                  console.error('解析批注位置失败:', comment.id, position, e);
                  position = null;
                }
              }
              
              mainComments.push({
                id: comment.id,
                content: comment.content,
                position: position,
                timestamp: comment.created_at || comment.timestamp,
                user_id: comment.created_by,
                user_name: comment.user_name || '匿名用户',
                replies: [],
              });
            } else {
              // 回复
              if (!replyMap[comment.reply_id]) {
                replyMap[comment.reply_id] = [];
              }
              replyMap[comment.reply_id].push({
                id: comment.id,
                content: comment.content,
                timestamp: comment.created_at || comment.timestamp,
                user_id: comment.user_id,
                user_name: comment.user_name || '匿名用户',
                reply_id: comment.reply_id,
              });
            }
          });

          // 将回复关联到主评论
          mainComments.forEach(comment => {
            if (replyMap[comment.id]) {
              comment.replies = replyMap[comment.id];
              // 按时间排序回复
              comment.replies.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            }
          });

          // 按时间倒序排列主评论（最新的在前）
          mainComments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

          this.annotations = mainComments;
          
          // 加载完批注后立即更新位置
          this.$nextTick(() => {
            this.updateAnnotationPositions();
          });
        } catch (error) {
          console.error('加载批注失败:', error);
          this.annotations = [];
        }
      },
  
      formatTime(timestamp) {
        const date = new Date(timestamp);
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      },

      formatTimeShort(timestamp) {
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return '刚刚';
        if (minutes < 60) return `${minutes}分钟前`;
        if (hours < 24) return `${hours}小时前`;
        if (days < 7) return `${days}天前`;
        
        return date.toLocaleDateString('zh-CN', {
          month: '2-digit',
          day: '2-digit',
        });
      },
    },
  };
  </script>
  
  <style scoped>
  .panorama-container {
    display: flex;
    flex-direction: column;
    background: #1a1a1a;
    overflow: hidden;
    position: relative;
  }
  
  /* 加载中样式 */
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #1a1a1a;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }
  
  .loading-spinner {
    width: 60px;
    height: 60px;
    border: 4px solid rgba(102, 126, 234, 0.2);
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  .loading-text {
    margin-top: 20px;
    color: #667eea;
    font-size: 16px;
    font-weight: 500;
  }

  .loading-progress {
    margin-top: 20px;
    width: 300px;
  }

  .progress-bar {
    width: 100%;
    height: 8px;
    background: rgba(102, 126, 234, 0.2);
    border-radius: 4px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  .progress-text {
    margin-top: 10px;
    color: #667eea;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
  }
  
  /* 错误样式 */
  .error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #1a1a1a;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 100;
    padding: 20px;
  }
  
  .error-icon {
    font-size: 64px;
    margin-bottom: 20px;
  }
  
  .error-text {
    color: #ff6b6b;
    font-size: 16px;
    text-align: center;
    margin-bottom: 20px;
    max-width: 400px;
  }
  
  .retry-btn {
    padding: 10px 24px;
    background: #667eea;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .retry-btn:hover {
    background: #5568d3;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  /* 全景图内容 */
  .panorama-content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  
  /* 底部浮动工具栏 */
  /* 模式切换按钮 */
  .mode-toggle-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 20;
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 20px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
    user-select: none;
  }

  .mode-toggle-btn:hover {
    background: rgba(0, 0, 0, 0.85);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
  }

  .mode-toggle-btn:active {
    transform: scale(0.95);
  }

  .floating-toolbar {
    position: absolute;
    bottom: 50px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 12px;
    z-index: 20;
    background: rgba(0, 0, 0, 0.5);
    padding: 12px;
    border-radius: 50px;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
  
  .toolbar-btn {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.1);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    transition: all 0.3s ease;
    backdrop-filter: blur(5px);
  }
  
  .toolbar-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.1);
  }
  
  .toolbar-btn.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-color: #667eea;
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.5);
  }
  
  .toolbar-btn .icon {
    display: inline-block;
  }
  
  .panorama-viewer {
    flex: 1;
    position: relative;
    overflow: hidden;
    background: #000;
  }
  
  canvas {
    display: block;
    width: 100%;
    height: 100%;
    cursor: grab;
  }
  
  canvas:active {
    cursor: grabbing;
  }
  
/* 新建批注弹窗（复用样式） */
  .annotation-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
  }
  
  .annotation-content {
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    animation: slideUp 0.3s ease;
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .annotation-header-modal {
    padding: 16px 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .annotation-header-modal h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
  
  .close-btn {
    background: none;
    border: none;
    color: white;
    font-size: 28px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background 0.2s;
  }
  
  .close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  .annotation-body {
    padding: 20px;
    max-height: 500px;
    overflow-y: auto;
  }
  
  /* 新建批注输入框 */
  .annotation-input {
    width: 100%;
    padding: 12px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    resize: vertical;
    transition: border-color 0.3s;
    box-sizing: border-box;
  }
  
  .annotation-input:focus {
    outline: none;
    border-color: #667eea;
  }
  
  .annotation-footer {
    padding: 16px 20px;
    background: #f8f9fa;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
  
  .btn-cancel,
  .btn-save {
    padding: 8px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .btn-cancel {
    background: #e0e0e0;
    color: #666;
  }
  
  .btn-cancel:hover {
    background: #d0d0d0;
  }
  
  .btn-save {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-weight: 600;
  }
  
  .btn-save:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  /* 提示横幅 */
  .tip-banner {
    position: absolute;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(102, 126, 234, 0.95);
    color: white;
    padding: 12px 24px;
    border-radius: 24px;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    z-index: 20;
    animation: fadeInDown 0.3s ease;
    backdrop-filter: blur(10px);
  }
  
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
  
/* 移动端适配 */
@media screen and (max-width: 768px) {
  .floating-toolbar {
    bottom: 16px;
    padding: 8px;
  }

  .toolbar-btn {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  .tip-banner {
    top: 16px;
    padding: 10px 20px;
    font-size: 13px;
    max-width: 90%;
  }

  .annotation-content {
    width: 95%;
  }

  /* 移动端批注卡片优化 */
  .annotation-card {
    max-width: 220px;
    min-width: 180px;
    padding: 8px;
  }

  .marker-pin {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }

  .annotation-content-preview {
    font-size: 12px;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .annotation-footer {
    font-size: 10px;
  }
}
  </style>
  
  